import { Injectable, NgZone } from "@angular/core";
import { WindowRef } from "@obg/ngx.global.evo";

import { IdleDeadline, RequestIdleOptions } from "./scheduler.model";

/**
 * `SchedulerService` enables the consumer to run a task
 * when the browser is in idle next time.
 *
 * It uses `requestIdleCallback` API when available. If a browser
 * does not support it, fallback to `setTimout` based on:
 * https://developer.mozilla.org/en-US/docs/Web/API/Background_Tasks_API#Falling_back_to_setTimeout
 */
@Injectable({
	providedIn: "root",
})
export class SchedulerService {
	private requestIdleCallback: (task: (idleDeadline?: IdleDeadline) => void, options?: RequestIdleOptions) => number;
	private cancelIdleCallback: (id: number) => void;

	constructor(
		private ngZone: NgZone,
		private window: WindowRef
	) {
		this.requestIdleCallback = (this.window.native as any).requestIdleCallback || this.getRequestIdlePolyfill();
		this.cancelIdleCallback = (this.window.native as any).cancelIdleCallback || this.getCancelIdlePolyfill();
	}

	requestIdle(task: (idleDeadline?: IdleDeadline) => void, options?: RequestIdleOptions): number {
		return this.requestIdleCallback.call(undefined, task, options);
	}

	cancelIdle(timerId: number) {
		this.cancelIdleCallback.call(undefined, timerId);
	}

	private getRequestIdlePolyfill() {
		return (handler: (idleDeadline?: IdleDeadline) => void, options?: RequestIdleOptions) => {
			const startTime = Date.now();

			return this.ngZone.runOutsideAngular(() =>
				setTimeout(
					() => {
						handler({
							didTimeout: false,
							timeRemaining: () => Math.max(0, 50.0 - (Date.now() - startTime)),
						});
					},
					options?.timeout ? options.timeout / 2 : 1
				)
			);
		};
	}

	private getCancelIdlePolyfill() {
		return (timerId: number) => this.ngZone.runOutsideAngular(() => clearTimeout(timerId));
	}
}
